import { Loader, ProcessDetailPlaceholder } from "@certa/blocks";
import {
  Paper,
  ProcessLogo,
  Stack,
  Text,
  ToggleTabsNew
} from "@certa/blocks/thanos";
import { Thunder } from "@certa/icons/components/Thunder";
import { useGetTaskDependencies } from "@certa/queries/hooks/tasks.hooks";
import {
  useGetProgressMap,
  useGetStepUserTags
} from "@certa/queries/hooks/workflow.hooks";
import { useGetWorkflowDetails } from "@certa/queries/queries/workflowDetails/getWorkflowDetails.query";
import { TaskCategoryTypes } from "@certa/tasks/src/types";
import { Divider } from "antd";
import { css } from "emotion";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router";
import type { TaskChangeArgs } from "../../types";
import { useTaskLaneUserFormatData } from "../hooks/taskConstraints";
import { useTaskUpdate } from "../hooks/taskUpdateSideEffects";
import { FlowDiagram } from "./FlowChart/FlowDiagram";
import { useTaskCategoryFilters } from "./FlowChart/hooks/useTaskCategoryFilters";
import {
  Button,
  ButtonSizes,
  ButtonTypes,
  ButtonVariants
} from "@certa/catalyst/components/Button";
import { Switch } from "@certa/catalyst/components/Switch";
import {
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";

import { Stack as CatalystStack } from "@certa/catalyst/layouts/Stack";
import { withErrorBoundary } from "@certa/common/components/ErrorBoundary";
import {
  getProcessDetailRoute,
  getTaskRoute
} from "@certa/common/utils/routes";
import { Close } from "@certa/icons/components/Close";

const TaskStepgroupOverlayComponent: FC<{
  processId: number;
  hideOverlay: () => void;
  height?: string;
  overviewPage?: boolean;
  // This prop is used to hide the close button in the overlay of antd
  // Once we migrate useTaskStepgroupOverlay to Dialog, we can remove this prop
  // https://thevetted.atlassian.net/browse/PLAT-28498
  shouldHideCloseButton?: boolean;
}> = ({
  processId,
  hideOverlay,
  height,
  overviewPage = false,
  shouldHideCloseButton = true
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const [canShowHiddenSteps, setCanShowHiddenSteps] = useState(false);

  const { data: taskDetails, status: taskStatus } =
    useGetWorkflowDetails(processId);

  const { data: taskLanes, status: taskLanesStatus } =
    useGetProgressMap(processId);

  const { data: stepUsers, status: stepUsersStatus } =
    useGetStepUserTags(processId);

  const { data: stepsDependencyData, status: stepsDependencyStatus } =
    useGetTaskDependencies(processId);

  const groupsList = taskLanes?.results;

  const isTaskRoute = location.pathname === getProcessDetailRoute(processId);

  const { handleTaskChange } = useTaskUpdate();
  const onTaskClick = (payload: TaskChangeArgs) => {
    // if on process details page
    if (isTaskRoute) handleTaskChange(payload);
    else {
      const { taskId, taskLaneId } = payload;
      navigate(
        getTaskRoute({
          processId: processId,
          taskLaneId,
          taskId
        })
      );
    }
    hideOverlay();
  };

  const taskLanesData = useTaskLaneUserFormatData(taskLanes, stepUsers);

  const isLoading =
    taskStatus === "idle" ||
    taskStatus === "loading" ||
    taskLanesStatus === "loading" ||
    stepUsersStatus === "idle" ||
    stepUsersStatus === "loading" ||
    stepsDependencyStatus === "idle" ||
    stepsDependencyStatus === "loading";

  const isError =
    taskStatus === "error" ||
    taskLanesStatus === "error" ||
    stepUsersStatus === "error";

  const filteredTaskLaneData = useMemo(
    () =>
      taskLanesData && {
        ...taskLanesData,
        results: taskLanesData.results.filter(
          group =>
            canShowHiddenSteps || group.steps.find(step => !step.isHidden)
        )
      },
    [canShowHiddenSteps, taskLanesData]
  );

  const {
    taskCategoryFilter,
    setTaskCategoryFilter,
    tasklaneFilteredByCategory,
    taggedStepGroups,
    taskCounts
  } = useTaskCategoryFilters({
    initialFilter: TaskCategoryTypes.ALL_TASKS,
    tasklane: filteredTaskLaneData
  });

  const onToggleShowHiddenSteps = (checked: boolean) => {
    analyticsService.track(
      AnalyticsEvents.tasksPageEvents
        .PROGRESS_MAP_CLICK_SHOW_INACTIVE_STEPS_TOGGLE,
      { checked }
    );
    setCanShowHiddenSteps(checked);
  };

  const onChangeToggleTabs = (value: string) => {
    analyticsService.track(
      AnalyticsEvents.tasksPageEvents.PROGRESS_MAP_CLICK_TAB,
      { tab: value }
    );
    setTaskCategoryFilter(value as TaskCategoryTypes);
  };

  const showInactiveStepsLabel = intl.formatMessage({
    id: "progressMap.showInactiveSteps",
    defaultMessage: "Show inactive steps"
  });

  if (isLoading)
    return (
      <Stack
        justify="center"
        align="center"
        className={css`
          min-height: 84px;
        `}
      >
        <Loader />
      </Stack>
    );

  if (isError)
    return (
      <Stack direction="vertical" align="center" justify="center">
        <Text variant="h2-regular" color="red">
          <FormattedMessage id="notificationInstances.somethingWentWrong" />
        </Text>
      </Stack>
    );

  return (
    <Stack direction="vertical">
      <Stack
        justify="space-between"
        align="center"
        className={
          !overviewPage
            ? css`
                padding: var(--s6) var(--s8) var(--s3) var(--s8);
              `
            : ""
        }
      >
        {!overviewPage && (
          <Stack gap="s2" align="center">
            {taskDetails?.logo ? (
              <ProcessLogo
                logoURL={taskDetails?.logo}
                name={taskDetails?.name}
              />
            ) : (
              <ProcessDetailPlaceholder />
            )}
            <Stack gap="s1" align="start" direction="vertical">
              <Text
                variant="h3-bold"
                color="neutral-100"
                useMaxWidth
                ellipsis={490} //renders 50 characters before ellipsing
              >
                {taskDetails?.name}
              </Text>
              <Text
                variant="h3-medium"
                color="neutral-70"
                useMaxWidth
                ellipsis={480} //renders 50 characters before ellipsing
              >
                {taskDetails?.definition.name}
              </Text>
            </Stack>
          </Stack>
        )}

        <CatalystStack
          gap="16"
          direction="horizontal"
          align="center"
          dangerouslySetClassName={
            overviewPage
              ? css`
                  margin-bottom: var(--s2);
                  margin-left: auto;
                  right: 0px;
                `
              : css`
                  margin-bottom: var(--s2);
                  ${shouldHideCloseButton && "margin-right: 50px !important;"}
                `
          }
        >
          <CatalystStack direction="horizontal" gap="8" align="center">
            <Switch
              label={showInactiveStepsLabel}
              onChange={onToggleShowHiddenSteps}
              checked={canShowHiddenSteps}
            />

            <Typography
              variant={TypographyVariants.OVERLINE_SMALL}
              color={TypographyColors.NEUTRAL_700}
              as="span"
            >
              {showInactiveStepsLabel}
            </Typography>
          </CatalystStack>

          {!isTaskRoute && (
            <Button
              variant={ButtonVariants.OUTLINE}
              onClick={() => {
                navigate(getProcessDetailRoute(processId));
                hideOverlay();
              }}
            >
              {intl.formatMessage({
                id: "process.viewDetails",
                defaultMessage: "View details"
              })}
            </Button>
          )}

          {!shouldHideCloseButton && (
            <Button
              type={ButtonTypes.ICON}
              size={ButtonSizes.SMALL}
              onClick={hideOverlay}
              leftIcon={<Close />}
            />
          )}
        </CatalystStack>
      </Stack>
      {!overviewPage && (
        <Divider
          className={css`
            margin-bottom: var(--s00);
          `}
        />
      )}
      {/* taskLanesData will be empty only when there is no permission to view the progressmap */}
      {taskLanesData ? (
        <>
          <Paper
            gutter="s1"
            className={css`
              position: absolute;
              top: ${overviewPage ? "var(--s10)" : "calc(var(--s20) + 26px)"};
              left: var(--s6);
              z-index: var(--zindex-fixed);
              padding: var(--s1);
              box-shadow: 0px 1px 3px rgba(0, 22, 78, 0.15);
              & + div {
                border-radius: var(--big-border-radius);
                border: var(--s0) solid var(--neutral-20);
              }
            `}
          >
            <ToggleTabsNew
              options={[
                {
                  label: "All",
                  value: TaskCategoryTypes.ALL_TASKS
                },
                {
                  label: "Waiting on me",
                  value: TaskCategoryTypes.ME,
                  icon: Thunder,
                  countBadgeProps: {
                    count: taskCounts.ME,
                    showZero: true
                  }
                },
                {
                  label: "Waiting on my groups",
                  value: TaskCategoryTypes.MY_GROUPS,
                  countBadgeProps: {
                    count: taskCounts.MY_GROUPS,
                    showZero: true
                  }
                }
              ]}
              onChange={onChangeToggleTabs}
            />
          </Paper>
          {tasklaneFilteredByCategory ? (
            <FlowDiagram
              taskCategoryFilter={taskCategoryFilter}
              onTaskClick={onTaskClick}
              taskLanesData={tasklaneFilteredByCategory}
              taggedStepGroups={taggedStepGroups}
              groupsList={groupsList}
              stepDependencies={stepsDependencyData}
              showHiddenSteps={canShowHiddenSteps}
              height={height}
            />
          ) : null}
        </>
      ) : (
        <Stack
          align="center"
          justify="center"
          className={css({
            // The flow diagram height is 65vh, so this is the same height as the flow diagram
            height: "65vh",
            padding: "var(--s8)"
          })}
        >
          <Text variant="h1-bold" align="center">
            {intl.formatMessage({
              id: "progressMap.noPermissions",
              defaultMessage:
                "You don't have permission to view the workflow map for this workflow. You can view the tasks of this workflow from the workflow details page."
            })}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

export const TaskStepgroupOverlay = withErrorBoundary(
  TaskStepgroupOverlayComponent
);
